'use client';

import { usePolling } from '@/lib/usePolling';
import { HeartPulse, AlertTriangle, Eye, ShieldCheck } from 'lucide-react';
import type { HealthSummary } from '@/lib/customers/health-types';
import Link from 'next/link';

const INITIAL: HealthSummary = {
  total_customers: 0,
  healthy: 0,
  watch: 0,
  at_risk: 0,
  critical: 0,
  average_score: 0,
  bottom_10: [],
};

export function CustomerHealthWidget() {
  const { data } = usePolling<HealthSummary>({ url: '/api/field/customers/health?view=summary', intervalMs: 300_000, initialValue: INITIAL });

  const totalScored = data.healthy + data.watch + data.at_risk + data.critical;
  const needsAttention = data.at_risk + data.critical;

  return (
    <div className="bg-bg-surface border border-border-subtle rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-rose-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Customer Health</h3>
        </div>
        {needsAttention > 0 && (
          <span className="text-xs text-amber-400 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {needsAttention} need attention
          </span>
        )}
      </div>

      {/* Score bar */}
      {totalScored > 0 ? (
        <>
          <div className="flex gap-0.5 h-2 rounded-full overflow-hidden">
            {data.healthy > 0 && (
              <div
                className="bg-emerald-500 transition-all duration-500"
                style={{ width: `${(data.healthy / totalScored) * 100}%` }}
                title={`Healthy: ${data.healthy}`}
              />
            )}
            {data.watch > 0 && (
              <div
                className="bg-amber-500 transition-all duration-500"
                style={{ width: `${(data.watch / totalScored) * 100}%` }}
                title={`Watch: ${data.watch}`}
              />
            )}
            {data.at_risk > 0 && (
              <div
                className="bg-orange-500 transition-all duration-500"
                style={{ width: `${(data.at_risk / totalScored) * 100}%` }}
                title={`At Risk: ${data.at_risk}`}
              />
            )}
            {data.critical > 0 && (
              <div
                className="bg-rose-500 transition-all duration-500"
                style={{ width: `${(data.critical / totalScored) * 100}%` }}
                title={`Critical: ${data.critical}`}
              />
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-500" />
                {data.healthy}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-amber-500" />
                {data.watch}
              </span>
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-orange-500" />
                {data.at_risk}
              </span>
              {data.critical > 0 && (
                <span className="flex items-center gap-1 text-rose-400">
                  <AlertTriangle className="w-3 h-3" />
                  {data.critical}
                </span>
              )}
            </div>
            <span className="tabular-nums">Avg: {data.average_score}</span>
          </div>

          {/* Bottom 3 customers needing attention */}
          {data.bottom_10.length > 0 && (
            <div className="space-y-1 pt-1 border-t border-border-subtle">
              {data.bottom_10.slice(0, 3).map((c) => (
                <Link
                  key={c.customer_id}
                  href={`/customers/${c.customer_id}`}
                  className="flex items-center justify-between text-xs hover:bg-white/5 rounded px-1.5 py-1 -mx-1.5 transition-colors"
                >
                  <span className="text-zinc-300 truncate">{c.customer_name}</span>
                  <span className={`tabular-nums font-medium ${
                    c.score < 30 ? 'text-rose-400' :
                    c.score < 50 ? 'text-orange-400' :
                    'text-amber-400'
                  }`}>
                    {c.score}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-zinc-500 text-pretty">Health scores will appear after the first daily calculation.</p>
      )}
    </div>
  );
}
